import type { AccessTier, ApiEndpoint, McpTool } from "./types";

export const ACCESS_HIERARCHY: Record<AccessTier, number> = {
  public: 1,
  sandbox: 2,
  partner: 3,
  production: 4
};

export function normalizeAccessTier(value: unknown, fallback: AccessTier = "sandbox"): AccessTier {
  if (typeof value !== "string") {
    return fallback;
  }

  const tier = value.trim().toLowerCase();
  return tier in ACCESS_HIERARCHY ? (tier as AccessTier) : fallback;
}

export function checkAccess(required: AccessTier, current: AccessTier): boolean {
  return ACCESS_HIERARCHY[current] >= ACCESS_HIERARCHY[required];
}

export function getAccessBadgeHTML(access: AccessTier): string {
  let label = "Public";
  if (access === "sandbox") label = "Sandbox Required";
  else if (access === "partner") label = "Partner Access Required";
  else if (access === "production") label = "Production Tenant Only";

  return `<span class="badge-access-tier access-${access}">${label}</span>`;
}

export function getGatedOverlayHTML(access: AccessTier): string {
  let label = "Sandbox Developer";
  let desc = "This resource requires email signup or partner access credentials.";
  let btnLabel = "Sign Up / Switch to Sandbox Role";

  if (access === "partner") {
    label = "Registered Partner";
    desc = "This resource requires a partner invitation or capacity provider access key.";
    btnLabel = "Activate Partner Role";
  } else if (access === "production") {
    label = "Production Tenant";
    desc = "This resource details live tenant production configurations and operator-only tools.";
    btnLabel = "Activate Production Role";
  }

  return `
    <div class="gated-overlay">
      <div class="gated-card">
        <div class="gated-card-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
          </svg>
        </div>
        <div class="gated-card-title">${label} Access Required</div>
        <p class="gated-card-desc">${desc}</p>
        <button class="gated-card-button" data-elevate-role="${access}">${btnLabel}</button>
        <div class="gated-warning-notice">Public preview only. Production partner/operator packs are served through authenticated protected routes.</div>
      </div>
    </div>
  `;
}

export function getItemAccess(
  type: "concept" | "model" | "api",
  id: string,
  context: {
    apiEndpoints: ApiEndpoint[];
    mcpTools: McpTool[];
  }
): AccessTier {
  if (type === "concept") {
    const publicGuides = [
      "overview",
      "sic-naics-classification",
      "environments",
      "authentication",
      "error-model",
      "idempotency",
      "dynamic-questions",
      "quote-bind-workflow",
      "mcp-overview",
      "what-is-mcp",
      "auth-overview",
      "permissions-scopes",
      "preview-confirm-pattern"
    ];
    if (publicGuides.includes(id)) {
      return "public";
    }

    const sandboxGuides = [
      "tenant-context",
      "correlation-ids",
      "pagination",
      "rate-limits",
      "sandbox-data",
      "changelog"
    ];
    if (sandboxGuides.includes(id)) {
      return "sandbox";
    }

    const partnerGuides = [
      "webhook-signatures",
      "webhook-events",
      "mga-onboarding",
      "account-hierarchy",
      "back-office-workflows",
      "lloyds-crs",
      "connect-ai",
      "oauth-discovery",
      "api-key-setup",
      "tool-naming",
      "troubleshooting",
      "mcp-mount-config",
      "mcp-mount-operator"
    ];
    if (partnerGuides.includes(id)) {
      return "partner";
    }

    return "sandbox";
  }

  if (type === "model") {
    return "sandbox";
  }

  if (type === "api") {
    const mcpTool = context.mcpTools.find((tool) => tool.id === id);
    if (mcpTool) {
      if (mcpTool.family === "Operator Agent Tools") {
        return "production";
      }
      return "partner";
    }

    const endpoint = context.apiEndpoints.find((endpoint) => endpoint.id === id);
    if (endpoint) {
      const publicEndpoints = [
        "get-health",
        "get-me",
        "list-programs",
        "get-program",
        "get-program-questions",
        "get-program-coverages",
        "get-program-rules",
        "get-program-documents",
        "get-program-jurisdictions",
        "create-classification",
        "get-classification",
        "submit-classification-feedback",
        "get-sic-code",
        "get-naics-code",
        "search-taxonomy"
      ];
      if (publicEndpoints.includes(id)) {
        return "public";
      }

      if (
        endpoint.tag === "Webhooks" ||
        endpoint.tag === "BDX / Bordereaux Reporting" ||
        endpoint.tag === "Binder & Reinsurance Compliance"
      ) {
        return "partner";
      }

      return "sandbox";
    }
  }

  return "public";
}
